import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController, ModalController, ToastController } from '@ionic/angular';
import { CookieService } from 'ngx-cookie-service';
import { ApiService } from './api.service';
import * as dayjs from 'dayjs';
import { ProjectsService } from './projects.service';

declare var bootstrap: any;



@Injectable({
  providedIn: 'root'
})

export class GlobalService {

  // Loaded from assets/version.json at startup (see LoadVersion) rather than hardcoded here, so
  // bumping that one file is enough to both display the version and detect a new deploy - see
  // new_version_available below.
  version_number = '';
  loading = null;
  user: any = null;
  total_seconds = 0;
  interval = null;
  timer_wall_start: number | null = null;
  timer_project: any = null;
  timer_project_type: string = ''; // what timer_project is: project, task or missing information
  clients: any = null;
  companies: any = null;
  team_members: any = null;

  // True once a poll of assets/version.json (cache-busted, so it always reflects what's actually
  // deployed) finds a version different from the one this tab loaded at startup - the navbar shows
  // a "new version available" banner with a reload button when this flips, rather than reloading
  // out from under the user and risking losing whatever form they've got open.
  new_version_available = false;
  private startup_version = '';
  private version_check_interval: any = null;
  private readonly VERSION_CHECK_INTERVAL_MS = 60 * 60 * 1000;

  constructor(private toastController: ToastController, private loadingController: LoadingController, private cookieService: CookieService, private apiService: ApiService, private router: Router, private modalController: ModalController, private ngZone: NgZone) {
    this.LoadVersion();
  }

  // Shared by every "Go to Folder" context-menu option outside the file explorer itself (email
  // attachments, missing info, step files, etc.) - those pages have no folder-navigation state of
  // their own, so this just routes to the explorer with the target folder as the URL fragment.
  // ExplorerComponent listens for this both on fresh load (window.location.hash) and while already
  // open (its own route.fragment subscription), so this works whether or not it's already the active
  // page - see the "View Folder"/notification "Files" handling for the same pattern. Every one of
  // these context menus lives inside a modal (or a page embedded in one), and modals sit outside the
  // router outlet - so navigating alone would just change the URL behind whatever's still on screen.
  // Dismissing the topmost modal (if any) first is what actually reveals the explorer.
  async GoToFileFolder(parent_folder_guid: string) {
    if (!parent_folder_guid) return;
    const top_modal = await this.modalController.getTop();
    if (top_modal) await this.modalController.dismiss();
    await this.router.navigate(['/file-explorer'], { fragment: parent_folder_guid });
  }

  // Fetches assets/version.json with a cache-busting query param so this always reads the version
  // actually deployed right now, not whatever the browser cached alongside the rest of the app shell.
  private async FetchDeployedVersion(): Promise<string> {
    try {
      const response = await fetch(`assets/version.json?_=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return '';
      const data = await response.json();
      return data?.version || '';
    } catch {
      return '';
    }
  }

  async LoadVersion() {
    const version = await this.FetchDeployedVersion();
    if (!version) return;
    this.version_number = version;
    this.startup_version = version;
    this.StartVersionCheckTimer();
  }

  StartVersionCheckTimer() {
    clearInterval(this.version_check_interval);
    this.version_check_interval = setInterval(() => this.CheckForNewVersion(), this.VERSION_CHECK_INTERVAL_MS);
  }

  async CheckForNewVersion() {
    if (this.new_version_available) return;
    const latest_version = await this.FetchDeployedVersion();
    if (latest_version && latest_version !== this.startup_version) {
      this.new_version_available = true;
      clearInterval(this.version_check_interval);
    }
  }

  ReloadForNewVersion() {
    window.location.reload();
  }


  //Returns the current users information that is saved in cookies
  async GetUser() {
    
    
    let user = await JSON.parse(this.cookieService.get("user"));
    if (user.is_admin == 0) user.is_admin = false;
    else user.is_admin = true;
    return user;
  }

  // ── Time permissions (same rules as the API's TimeAndBillingAccess) ──
  // All independent of each other; admins have all of them (Can). time_billing_view: billing rates and the billing side of time
  // entries (billable, rate, amount). time_own_*: create / edit / delete time for themselves. time_others_*: see, and create /
  // edit / delete, other team members' time.
  get CanViewBillingInfo(): boolean {
    return this.Can('time_billing_view');
  }

  get CanViewOthersTime(): boolean {
    return this.Can('time_others_view');
  }

  // Whether the signed-in team member may create time for this team member (their own, or someone else's)
  CanCreateTimeFor(member_guid: string): boolean {
    return this.Can(member_guid === this.GetCurrentUser()?.guid ? 'time_own_create' : 'time_others_create');
  }

  // Whether the signed-in team member may edit / change the status of this time entry (an entry has its owner in user_guid)
  CanChangeTimeEntry(entry: any): boolean {
    return !entry || this.Can(entry.user_guid === this.GetCurrentUser()?.guid ? 'time_own_update' : 'time_others_update');
  }

  CanDeleteTimeEntry(entry: any): boolean {
    return !entry || this.Can(entry.user_guid === this.GetCurrentUser()?.guid ? 'time_own_delete' : 'time_others_delete');
  }

  // Whether they may create any time at all (for themselves or for someone else)
  get CanCreateAnyTime(): boolean {
    return this.Can('time_own_create') || this.Can('time_others_create');
  }

  // Time tracking and billing is switched on per firm (firms.allow_time_and_billing, set in the firm manager). Off unless
  // the signed-in user's firm has it set to 1 - the flag comes with the user at login, so a user who signed in before it
  // existed has to sign in again to see it.
  get TimeAndBillingEnabled(): boolean {
    const flag = this.GetCurrentUser()?.allow_time_and_billing;
    return flag === true || flag === 1 || flag === '1' || flag === 'true' || flag === 'True';
  }

  GetCurrentUser() {
    if (this.user) return this.user;
    let user_str = this.cookieService.get("user");
    
    if (user_str == null || user_str == "") return null;
    let user = JSON.parse(user_str);
    if (user.is_admin == 0) user.is_admin = false;
    else user.is_admin = true;
        
    this.user = user;
    
    return user;
  }

  // ── Team member permissions (API: UserPermissions.cs, permissions/me) ──
  // Firm admins always have every permission. These defaults match the API's defaults for a non-admin
  // whose permissions haven't been edited yet, and are only used until the real ones have loaded.
  static readonly DEFAULT_PERMISSIONS: { [key: string]: boolean } = {
    contacts_create: true, contacts_update: true, contacts_delete: true,
    companies_create: true, companies_update: true, companies_delete: true, 
    tags_create: true, tags_update: true, tags_delete: true,
    statuses_create: true, statuses_update: true, statuses_delete: true,
    project_templates_create: true, project_templates_update: true, project_templates_delete: true,
    document_templates_create: true, document_templates_update: true, document_templates_delete: true,
    form_templates_create: true, form_templates_update: true, form_templates_delete: true,
    email_templates_create: true, email_templates_update: true, email_templates_delete: true,
    contact_roles_create: true, contact_roles_update: true, contact_roles_delete: true,
    contacts_manage_custom_fields: true, companies_manage_custom_fields: true, projects_create: true, projects_update: true, projects_delete: true,
    projects_manage_health: true, projects_manage_schedules: true,
    contacts_manage_default_folders: true, companies_manage_default_folders: true,
    billing_create: false, billing_read: false, billing_update: false, billing_delete: false,
    team_members_create: false, team_members_update: false, team_members_delete: false,
    groups_create: true, groups_update: true, groups_delete: true,
    activity_log_read: false,
    contacts_manage_access: false, companies_manage_access: false,
    schedule_templates_read: true, schedule_templates_update: true,
    billing_rates_create: true, billing_rates_update: true, billing_rates_delete: true,
    work_codes_create: true, work_codes_update: true, work_codes_delete: true,
    time_tags_create: true, time_tags_update: true, time_tags_delete: true,
    time_billing_view: true,
    time_own_create: true, time_own_update: false, time_own_delete: false,
    time_others_view: false, time_others_create: false, time_others_update: false, time_others_delete: false,
  };
  private permissions: { [key: string]: boolean } = null;
  private permissions_user_guid = '';
  private permissions_loading = false;
  private permissions_retry_at = 0;

  // True if the signed-in team member has this permission. Safe to call from templates: the first call loads
  // the permissions in the background and answers with the defaults until they arrive.
  Can(permission: string): boolean {
    const user = this.GetCurrentUser();
    if (!user) return false;
    if (user.is_admin) return true;
    if (this.permissions_user_guid !== user.guid) {
      this.permissions = null;
      this.permissions_user_guid = user.guid;
    }
    if (!this.permissions && !this.permissions_loading && Date.now() >= this.permissions_retry_at) this.LoadPermissions();
    const source = this.permissions || GlobalService.DEFAULT_PERMISSIONS;
    if (source[permission]) return true;
    // Temporary UI rule: create implies edit for contacts and companies. Only applied here - the stored
    // permissions (and the API) are unchanged, so removing this restores the separate edit permission.
    const implied_by = GlobalService.IMPLIED_BY[permission];
    return !!implied_by && !!source[implied_by];
  }

  private static readonly IMPLIED_BY: { [key: string]: string } = {
    contacts_update: 'contacts_create',
    companies_update: 'companies_create',
  };

  // True if they have at least one of these (e.g. any tag permission opens the Manage Tags screen)
  CanAny(...permissions: string[]): boolean {
    return permissions.some(p => this.Can(p));
  }

  async LoadPermissions() {
    // Only ever store the result for the user it was requested for - if someone else signs in while this is in
    // flight, the response is thrown away and the next Can() loads theirs instead
    const requested_for = this.permissions_user_guid;
    this.permissions_loading = true;
    let loaded: any = null;
    try {
      const data: any = await this.apiService.get('permissions/me');
      // The API echoes whose permissions these are - ignore them if they aren't this user's
      if (data?.success && data.data?.[0] && data.data[0].user_guid === requested_for) loaded = data.data[0];
    } catch { }
    finally {
      this.permissions_loading = false;
    }
    if (this.permissions_user_guid !== requested_for) return;
    // On a failed load don't cache the defaults: Can() keeps answering with them for now, but only retries after a
    // pause rather than on every change-detection pass
    if (loaded) this.permissions = loaded;
    else this.permissions_retry_at = Date.now() + 30000;
  }

  async UpdateUser(user) {
    this.user = user;
    this.cookieService.set('user', JSON.stringify(user));
    var return_user = JSON.parse(this.cookieService.get('user'));
    return this.cookieService.get('user');
  }

  async presentToastMessage(message, success) {
    let color = 'danger';
    if (success) color = 'success';
    const toast = await this.toastController.create({
      message: message,
      position: 'top',
      duration: 3000,
      color: color,
    })
    await toast.present()
  }

  validateEmail(email) {
    const regularExpression = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regularExpression.test(String(email).toLowerCase());
  }

  validatePassword(password: string): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('At least 1 uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('At least 1 lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('At least 1 number');
    if (!/[^A-Za-z0-9]/.test(password)) errors.push('At least 1 special character');
    return { valid: errors.length === 0, errors };
  }

  createGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0,
        v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  isGUID(uuid) {
    const s = "" + uuid;
    const regularExpression = s.match('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');
    if (regularExpression === null) {
      return false;
    }
    return true;
  }

  //File helpers
  GetPathName(value) {
    return value.replace(/^.*(\\|\/|\:)/, '');
  }
  GetPathNameTwo(obj) {
    if (obj.folder_title) return obj.folder_title;
    else return this.GetPathName(obj.path);
  }
  // Resolves the "Included Clients" list for a calendar event fetched from GET calendar/events/{guid}.
  // Each non-team-member entry in event.users is a real contact (user_guid is always resolved server
  // side - see Calendar.Events.AssignUsers/Users.AssignUser) - company_guid/company_name are set
  // alongside it when that contact is standing in as a company's default contact, in which case it's
  // shown as "Company Name (Contact Name)" rather than just the contact's own name, since the
  // appointment was booked on the company's behalf even though a specific contact is the one who
  // actually booked it. Falls back to scheduler_guid/scheduler_name (the booking contact's own name,
  // company-unaware) only for appointments booked before calendar_event_users carried this link.
  ResolveAssignedClients(event: any): any[] {
    const other_users = (event?.users || []).filter((u: any) => u.type != 1);
    if (other_users.length) {
      return other_users.map((u: any) => ({
        guid: u.guid,
        display_name: u.company_guid ? `${u.company_name} (${u.display_name})` : u.display_name
      }));
    }
    if (event?.scheduler_guid) return [{ guid: event.scheduler_guid, display_name: event.scheduler_name }];
    return [];
  }

  GetFileType(value) {
    var patt1 = /\.([0-9a-z]+)(?:[\?#]|$)/i;
    var m1 = (value).match(patt1)[1];
    return m1;
  }

  //Loading Controller Present
  // Creating the overlay is async. If something asks to dismiss it before it has finished being created (a fast response after a
  // presentLoading that wasn't awaited), the dismiss used to find nothing to dismiss - and the overlay then appeared and stayed
  // on screen. This tracks the creation so a dismiss waits for it.
  private loading_pending: Promise<void> | null = null;

  async presentLoading(message, duration) {
    if (this.loading == null && !this.loading_pending) {
      this.loading_pending = (async () => {
        const overlay = await this.loadingController.create({
          cssClass: 'my-custom-class',
          message: message,
          duration: duration
        });
        this.loading = overlay;
        await overlay.present();
      })();
      try { await this.loading_pending; } finally { this.loading_pending = null; }
    }
    else if (this.loading != null) {
      this.loading.message = message;
    }
    else if (this.loading_pending) {
      await this.loading_pending;
    }
  }

  async dismissLoading() {
    if (this.loading_pending) {
      try { await this.loading_pending; } catch { }
    }
    if (this.loading != null) {
      this.loading.dismiss();
      this.loading = null;
    }
  }


  //Timer Logic
  async CheckTimer() {
    const cookie_timer = this.cookieService.get('timer');
    const timer_running = this.cookieService.get('timer_running');
    const wall_start = this.cookieService.get('timer_wall_start');
    if (cookie_timer) {
      this.total_seconds = parseInt(cookie_timer);
      if (wall_start) this.timer_wall_start = parseInt(wall_start);
      if (timer_running) this.StartTimer();
    }
  }


  convertMsToTime(milliseconds) {
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    let hours = Math.floor(minutes / 60);

    seconds = seconds % 60;
    minutes = minutes % 60;

    // 👇️ If you don't want to roll hours over, e.g. 24 to 00
    // 👇️ comment (or remove) the line below
    // commenting next line gets you `24:00:00` instead of `00:00:00`
    // or `36:15:31` instead of `12:15:31`, etc.
    hours = hours % 24;

    return `${this.padTo2Digits(hours)}:${this.padTo2Digits(minutes)}:${this.padTo2Digits(
      seconds,
    )}`;
  }
  padTo2Digits(num) {
    return num.toString().padStart(2, '0');
  }

  // When the timer was first started. The time it actually counted (total_seconds, which leaves out any time it
  // was paused) is passed to the time entry separately as the time worked between that start and the end.
  GetTimerStart(end: Date): Date {
    return this.timer_wall_start ? new Date(this.timer_wall_start) : new Date(end.getTime() - this.total_seconds);
  }

  async ResetTimer() {
    this.StopTimer();
    this.cookieService.set('timer', '', 0);
    this.cookieService.set('timer_wall_start', '', 0);
    this.total_seconds = 0;
    this.timer_wall_start = null;
    this.timer_project = null;
    this.timer_project_type = '';
  }

  async StartTimer() {
    this.cookieService.set('timer_running', '1', 0);
    if (!this.timer_wall_start) {
      this.timer_wall_start = Date.now() - this.total_seconds;
      this.cookieService.set('timer_wall_start', String(this.timer_wall_start), 0);
    }
    if (!this.interval) {
      this.interval = setInterval(() => {
        if (this.cookieService.get('timer_running')) {
          this.total_seconds += 1000;
          this.cookieService.set('timer', this.total_seconds.toString(), 0);
        } else {
          this.StopTimer();
        }
      }, 1000);
    }
  }

  async StopTimer() {
    clearInterval(this.interval);
    this.cookieService.set('timer_running', '', 0);
    this.interval = null;
  }

  RemoveFromArray(arr, guid) {
    var index = arr.findIndex(item => item.guid === guid);
    arr.splice(index, 1);
  }

  GetArrayIndex(arr, guid) {
    return arr.findIndex(item => item.guid === guid);
  }


  EnumToArrayTwo(enumme) {

    let map: { value: number; name: string }[] = [];
    for (var n in enumme) {
      if (typeof enumme[n] === 'number') {
        map.push({ value: <any>enumme[n], name: n });
      }
    }
    return map;
  }





  EnumToArray(enumme) {
    return Object.keys(enumme)
      .filter(value => isNaN(Number(value)) === false)
      .map(key => enumme[key]);
  }










  async GetFutureCalendarEvents(start_time, end_time, url_mod) {
    let return_data = [];
    url_mod = this.trimLeadingAmpersand(url_mod);
    let calendar_event_schedule: any = await this.apiService.get(`calendar/events/schedule?${url_mod}`);
    calendar_event_schedule = calendar_event_schedule.data;
    let schedule_data = calendar_event_schedule;
    if (!schedule_data) return null;

    for (var i = 0; i < schedule_data.length; i++) {
      var item = schedule_data[i];
      this.CreateCalendarEvent(return_data, item, start_time, end_time);

    }
    return return_data;
  }

  CreateCalendarEvent(return_data, item, _start_time, _end_time) {
    let schedule_json = JSON.parse(item.event_json);
    let last_days_to_add = 0;
    let not_at_end = true;
    while (not_at_end) {

      var days_to_add = item.interval;
      var type_mod = 'day';
      if (item.pattern == 'daily') type_mod = 'day';
      else if (item.pattern == 'monthly') type_mod = 'month';
      else if (item.pattern == 'yearly') type_mod = 'year';


      days_to_add = item.interval + last_days_to_add;
      last_days_to_add = days_to_add;
      var next_date = dayjs().startOf('day').add(days_to_add, type_mod);

      const orgStartTime = schedule_json.start_time;
      const orgEndTime = schedule_json.end_time;


      let dateReference = dayjs(orgStartTime);

      item.next_create_date = this.replaceTime(item.next_create_date, schedule_json.start_time);
      var start_time = dayjs(item.next_create_date).add(dayjs(orgStartTime).diff(dateReference, 'day'), 'day').add(days_to_add, type_mod).toISOString();
      var end_time = dayjs(item.next_create_date).add(dayjs(orgEndTime).diff(dateReference, 'day'), 'day').add(days_to_add, type_mod).toISOString();

      if (dayjs(start_time).startOf('day') > dayjs(_end_time).startOf('day')) {
        not_at_end = false;
        continue;
      }



      var event = {
        created_date: next_date.format('YYYY-MM-DD') + "T00:00:00",
        description: schedule_json.description,
        end_time: end_time,
        start_time: start_time,
        guid: item.guid + start_time,
        title: schedule_json.title,
        is_fake: true,
        scheduler_guid: item.guid,
        users: item.users,
        status: -1
      }


      let add_project = true;
      if (dayjs(event.start_time).isBefore(_start_time) || dayjs(event.end_time).isAfter(_end_time)) add_project = false;
      if (return_data.findIndex(item => item.guid === event.guid) == -1 && add_project) {
        return_data.push(JSON.parse(JSON.stringify(event)));
      }
    }

    return return_data;
  }

  async GetClient(guid) {
    if (this.clients == null) {
      this.clients = await this.apiService.GetClients();
      this.clients = this.clients.data;
    }
    var client = this.clients[this.clients.findIndex(item => item.guid === guid)];
    if (!client) {
      client = await this.apiService.get(`clients/${guid}`);
      if(!client.data) return null;
      if(!client.data[0]) return null;
      client = client.data[0];
      if (!client) return null;
      else this.clients.push(client);
    }
    return client;
  }

  async GetTeamMembersList() {
    if (this.team_members == null) {
      let data: any = await this.apiService.get('team_members');
      this.team_members = data.data;
    }
    return this.team_members;
  }

  async GetCompany(guid) {
    if (this.companies == null) {
      this.companies = await this.apiService.GetCompanies();
      this.companies = this.companies.data;
    }
    var company = this.companies[this.companies.findIndex(item => item.guid === guid)];
    if (!company) {
      company = await this.apiService.get(`companies/${guid}`);
      console.log("Company", company);
      if(!company.data) return null;
      if(!company.data[0]) return null;
      company = company.data[0];
      if (!company) return null;
      else this.companies.push(company);
    }
    return company;
  }



  async GetCalendarEvents(start_time, end_time, url_mod) {
    let calendar_event_data = [];
    let data: any = await this.apiService.get(`calendar/events/?start_time=${start_time}&end_time=${end_time}${url_mod}`);
    if (data.success && data.data) {
      for (const item of data.data) {
        if (item) {
          item.generated_id = item.scheduler_guid + this.nullToEmpty(item.title) + dayjs(item.end_time).format('YYYY-MM-DD') + dayjs(item.start_time).format('YYYY-MM-DD') + "T00:00:00";
          calendar_event_data.push(item);
        }
      }
    } else if (!data.success) {
      this.presentToastMessage(data.message, data.success);
      return;
    }
    

    //Need to solve the future events.
    let generated_events = await this.GetFutureCalendarEvents(start_time, end_time, url_mod);
    if (generated_events) {
      for (const item of generated_events) {
        if (item) {
          item.generated_id = item.scheduler_guid + this.nullToEmpty(item.title) + dayjs(item.end_time).format('YYYY-MM-DD') + dayjs(item.start_time).format('YYYY-MM-DD') + "T00:00:00";
          let allowed = true;
          for (const proj of calendar_event_data) {
            if (item.generated_id == proj.generated_id) {
              allowed = false;
              break;
            }
          }
          if (allowed) {
            calendar_event_data.push(item);
          }
        }
      }
    }

    return calendar_event_data;
  }




  async waitForSeconds(seconds): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, seconds * 1000));
  }

  GetTimeDifference(startDate: string, endDate: string): string {
    // Convert the string to Date objects
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Get the difference in milliseconds
    const diffInMs = end.getTime() - start.getTime();

    // Convert milliseconds to minutes
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    // Get hours and minutes
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;

    const hourText = hours === 1 ? 'hour' : 'hours';
    const minuteText = minutes === 1 ? 'minute' : 'minutes';

    return `${hours} ${hourText}, ${minutes} ${minuteText}`;
  }

  CalculateBillableAmount(startDate: string, endDate: string, rate: number): string {
    // Convert strings to Date objects
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate time difference in milliseconds
    const diffInMs = end.getTime() - start.getTime();

    // Convert milliseconds to total minutes
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    // Get hours and minutes
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;

    // Calculate total time in decimal hours
    const totalTimeInHours = hours + minutes / 60;

    // Calculate the total bill
    const totalBill = totalTimeInHours * rate;

    return totalBill.toFixed(2);
  }

  MakeJSONCopy(val) {
    return JSON.parse(JSON.stringify(val));
  }

  Stringify(val) {
    return JSON.stringify(val);
  }


  ConvertDateFromBackend = (dateTimeString: string | null | undefined): string | null | undefined => {
    // Check if the input is null, undefined, or an empty string
    if (!dateTimeString) {
      return dateTimeString; // Return the input as is
    }

    // Check if the string contains 'T'
    if (!dateTimeString.includes('T')) {
      return dateTimeString; // Return the input as is
    }

    // Split the string by 'T' and return the date part
    const [datePart] = dateTimeString.split('T');
    return datePart;
  };

  DateToUTCFormat = (dateTimeString: string | Date | null | undefined): string | null | undefined => {
    if (!dateTimeString) return dateTimeString as null | undefined;
    if (dateTimeString instanceof Date) return dateTimeString.toISOString();
    return dateTimeString + "Z";
  };


  nullToEmpty(val) {
    if (val) return val;
    else return "";
  }

  GetVariableId(url: string, variableName: string): string | null {
    const regex = new RegExp(`${variableName}=([^&]+)`);
    const match = url.match(regex);
    return match ? match[1] : null;
  }

  trimLeadingAmpersand(str: string): string {
    return str.startsWith("&") ? str.slice(1) : str;
  }

  


  replaceTime(dateWithDate: string, dateWithTime: string): string {
    // Parse the string dates into Date objects
    const datePart = new Date(dateWithDate);
    const timePart = new Date(dateWithTime);

    // Extract date parts from the first date
    const year = datePart.getFullYear();
    const month = datePart.getMonth(); // 0-based
    const day = datePart.getDate();

    // Extract time parts from the second date
    const hours = timePart.getHours();
    const minutes = timePart.getMinutes();
    const seconds = timePart.getSeconds();
    const milliseconds = timePart.getMilliseconds();

    // Combine date and time into a new Date object
    const combinedDate = new Date(year, month, day, hours, minutes, seconds, milliseconds);

    // Convert back to ISO string format
    return combinedDate.toISOString();
  }

  GetURLParameter(name: string): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  FormatPhoneNumber(phone_number) {
    let input = phone_number.replace(/\D/g, ''); // Remove all non-numeric characters

    if (input.length > 10) {
      input = input.substring(0, 10); // Limit to 10 digits
    }

    // Format the number as (555) 555-5555
    let formattedNumber = '';
    if (input.length > 0) {
      formattedNumber += '(' + input.substring(0, 3);
    }
    if (input.length >= 4) {
      formattedNumber += ') ' + input.substring(3, 6);
    }
    if (input.length >= 7) {
      formattedNumber += '-' + input.substring(6, 10);
    }

    return formattedNumber;
  }

  openWebsite(_website): void {
    
    if (_website) {
      let url = _website.trim();
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url; // Default to HTTPS if no scheme is provided
      }
      window.open(url, '_blank');
    }
  }

  //Restore anything that is not an array
  RestoreBasicInfo(source: any, target: any): void {
    for (const key in source) {
      const sourceValue = source[key];
      if (target.hasOwnProperty(key)) {
        if (!Array.isArray(target[key])) {
          target[key] = sourceValue;
        }
      }

    }
  }

  CopyBasicInfo(source: any): any {
    const target: any = {};
    for (const key in source) {
      if (!Array.isArray(source[key])) {
        target[key] = source[key];
      }
    }
    return target;
  }


  private currentTooltip: any = null;

  private getTooltipElement(event: Event): HTMLElement | null {
    const currentTarget = event.currentTarget;
    if (currentTarget instanceof HTMLElement) {
      return currentTarget;
    }

    const target = event.target;
    if (target instanceof HTMLElement) {
      return target;
    }

    return null;
  }

  ShowTooltip(event: Event, message: string): void {
    const el = this.getTooltipElement(event);
    if(!el) return;
    this.OpenTooltip(el, message, 'top');
  }

  // Shows the one global tooltip on an element. Two things keep hovering cheap and error free:
  //  - animation: false - with the fade on, leaving before it finished disposed the tooltip while Bootstrap's
  //    end-of-fade callback was still queued, and that callback then failed on the disposed tooltip
  //    ("Cannot read properties of null (reading 'template')").
  //  - created outside Angular's zone - Bootstrap's own hover/focus listeners and timers on the element would
  //    otherwise each trigger a full change detection pass across the app.
  private OpenTooltip(el: HTMLElement, message: string, placement: string, customClass: string = ''): void {
    this.HideTooltip(); // Clean up any previous tooltip
    if (message == null || message == "") return;

    // Bootstrap moves this into data-bs-original-title (and blanks title, so the browser's own tooltip
    // doesn't show as well) - set every time since the message can change between hovers
    el.setAttribute('title', message);

    this.ngZone.runOutsideAngular(() => {
      this.currentTooltip = new bootstrap.Tooltip(el, {
        html: true,
        placement,
        animation: false,
        customClass
      });
      this.currentTooltip.show();
    });
  }

  ShowtoolTipWithPosition(event: Event, message: string, position: string, width: string): void {
    const el = this.getTooltipElement(event);
    if(!el) return;

    if (message == null || message == "") {
      this.HideTooltip();
      return;
    }

    // Apply requested width to the tooltip with a custom class on the tooltip element
    // and a small injected CSS rule that targets its tooltip-inner.
    const widthValue = width ? String(width) : '200px';
    const widthCss = /^\d+$/.test(widthValue) ? `${widthValue}px` : widthValue;
    const clsSafe = widthCss.replace(/[^a-zA-Z0-9]/g, '_');
    const customClass = `tooltip-width-${clsSafe}`;

    // Inject a stylesheet for this class only once
    const styleId = `tooltip-width-style-${clsSafe}`;
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
      .${customClass} .tooltip-inner {
        max-width: ${widthCss} !important;
        width: ${widthCss} !important;
      }
      `;
      document.head.appendChild(style);
    }

    this.OpenTooltip(el, message, position || 'top', customClass);
  }

  ShowTooltipConditional(event: Event, message: string, condition: boolean): void {
    if (!condition) return;

    const el = this.getTooltipElement(event);
    if(!el) return;
    this.OpenTooltip(el, message, 'top');
  }

  HideTooltip(): void {
    if (this.currentTooltip) {
      try {
        const tipElement = this.currentTooltip.getTipElement?.();
        this.currentTooltip.dispose?.();

        if (tipElement?.parentNode) {
          tipElement.parentNode.removeChild(tipElement);
        }
      } catch (err) {
        // Silently suppress all errors
      }

      this.currentTooltip = null;
    }
  }

  ReturnFormattedDate(date: string) {
    const today = new Date(date);
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  GetDayOrdinal(dateIn) {
    const weekday = dateIn.day(); // 0 (Sun) - 6 (Sat)
    const month = dateIn.month();
    const year = dateIn.year();

    // Find all dates in this month that are the same weekday
    let count = 0;
    let lastMatch = null;
    for (let d = 1; d <= dateIn.daysInMonth(); d++) {
      const date = dayjs(new Date(year, month, d));
      if (date.day() === weekday) {
        count++;
        lastMatch = d;
        if (d === dateIn.date()) {
          // Found our date, break if not looking for last
          break;
        }
      }
    }

    // Otherwise, check ordinal
    const ordinal = Math.ceil(dateIn.date() / 7).toString();
    return ordinal;
  }

  GetGroupPath(groups,group) {
    //This needs to loop through all the groups and keep appending the titles of the parents until it reaches the top.
      if (!group) return '';
      // If no parent, just return the group's title
      if (!group.parent_guid) return group.title || '';

      const allGroups = groups;

      const visited = new Set<string>();
      const buildPath = (g: any): string => {
        if (!g) return '';
        // Prevent cycles
        if (visited.has(g.guid)) return g.title || '';
        visited.add(g.guid);

        if (!g.parent_guid) return g.title || '';

        const parent = allGroups.find(p => p && p.guid === g.parent_guid);
        if (!parent) return g.title || '';

        const parentPath = buildPath(parent);
        return parentPath ? `${parentPath} -> ${g.title || ''}` : `${parent.title || ''} -> ${g.title || ''}`;
      };

      return buildPath(group);
  }

  /**
   * Get time difference as an object with hours and minutes
   * @param startDate Start date/time
   * @param endDate End date/time
   * @returns Object with hours and minutes
   */
  GetTimeDifferenceAsObject(startDate: string | Date, endDate: string | Date): { hours: number, minutes: number } {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, minutes };
  }


}




